import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Stethoscope, X, RefreshCw, AlertTriangle } from "lucide-react";


/** Modal that fetches an AI diagnosis of a strategy and renders it. */
export function StrategyDoctorModal({ strategyId, onClose }) {
  const [dx, setDx] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const run = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const { data } = await api.post("/strategy-doctor", { strategy_id: strategyId });
      setDx(data.result || data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Doctor unavailable");
      toast.error("Doctor call failed");
    } finally { setBusy(false); }
  }, [strategyId]);

  useEffect(() => { run(); }, [run]);

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="strategy-doctor-modal">
      <div className="bg-[var(--bg-card)] border border-white/10 rounded max-w-2xl w-full max-h-[80vh] overflow-auto">
        <header className="p-4 border-b border-white/10 flex items-center gap-2">
          <Stethoscope className="text-[var(--accent)]" size={18}/>
          <h2 className="font-display text-lg text-white">Strategy Doctor</h2>
          <button data-testid="doctor-refresh" onClick={run} disabled={busy}
            className="ml-auto btn-outline !py-1 !px-2 text-xs flex items-center gap-1">
            {busy ? <RefreshCw size={12} className="animate-spin"/> : <RefreshCw size={12}/>} Re-analyze
          </button>
          <button data-testid="doctor-close" onClick={onClose}
            className="p-1.5 border border-white/10 rounded hover:border-[var(--loss)] hover:text-[var(--loss)]">
            <X size={13}/>
          </button>
        </header>

        <div className="p-5 space-y-4">
          {busy && !dx && <p className="text-[var(--text-muted)] font-mono">Consulting Claude…</p>}
          {err && <p className="text-[var(--loss)] font-mono text-xs">{err}</p>}
          {dx && <DiagnosisView dx={dx}/>}
        </div>
      </div>
    </div>
  );
}


function DiagnosisView({ dx }) {
  const prescriptions = dx.prescriptions || [];
  return (
    <div className="space-y-4" data-testid="doctor-diagnosis">
      {dx.diagnosis && (
        <div className="border-l-2 border-[var(--accent)] pl-3 py-1">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Diagnosis</div>
          <p className="text-sm text-white">{dx.diagnosis}</p>
        </div>
      )}
      {prescriptions.length > 0 && (
        <ul className="space-y-3">
          {prescriptions.map((p, i) => (
            <li key={`rx-${i}`} className="border border-white/10 rounded p-3" data-testid={`doctor-rx-${i}`}>
              <div className="flex items-center gap-2 mb-1">
                <AlertTriangle size={12}
                  className={p.severity === "high" ? "text-[var(--loss)]" :
                             p.severity === "med" ? "text-[var(--warning)]" : "text-[var(--text-muted)]"}/>
                <span className="text-xs font-mono uppercase tracking-widest text-white">{p.issue}</span>
                <span className={`chip ml-auto ${
                  p.severity === "high" ? "!text-[var(--loss)]" :
                  p.severity === "med" ? "!text-[var(--warning)]" : "!text-[var(--text-muted)]"
                }`}>{p.severity || "info"}</span>
              </div>
              <p className="text-xs text-[var(--text-secondary)] mb-2">{p.why}</p>
              <p className="text-xs font-mono text-[var(--accent)]">▸ {p.fix}</p>
            </li>
          ))}
        </ul>
      )}
      {dx.expected_impact && (
        <div className="border border-[var(--profit)]/20 bg-[var(--profit)]/5 rounded p-3">
          <div className="text-[10px] font-mono uppercase tracking-widest text-[var(--text-muted)] mb-1">Expected impact</div>
          <p className="text-xs text-[var(--profit)]">{dx.expected_impact}</p>
        </div>
      )}
    </div>
  );
}
